import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../api/api';
import './chat.css';

const toShortDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString();
};

const toInitials = (value) => {
    const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
    return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
};

const isMobileDevice = () => {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
        if (window.matchMedia('(max-width: 900px)').matches) return true;
        if (window.matchMedia('(pointer: coarse)').matches) return true;
    }
    if (typeof navigator === 'undefined') return false;
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
};

const GuestChatPage = () => {
    const { conversationId } = useParams();
    const [searchParams] = useSearchParams();
    const token = String(searchParams.get('token') || '').trim();
    const [conversation, setConversation] = useState(null);
    const [listing, setListing] = useState(null);
    const [messages, setMessages] = useState([]);
    const [composer, setComposer] = useState('');
    const [error, setError] = useState('');
    const [recipientPhone, setRecipientPhone] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const streamRef = useRef(null);

    useEffect(() => {
        const updateMobile = () => setIsMobile(isMobileDevice());
        updateMobile();

        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const mediaQuery = window.matchMedia('(max-width: 900px), (pointer: coarse)');

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', updateMobile);
            return () => mediaQuery.removeEventListener('change', updateMobile);
        }

        if (typeof mediaQuery.addListener === 'function') {
            mediaQuery.addListener(updateMobile);
            return () => mediaQuery.removeListener(updateMobile);
        }

        return undefined;
    }, []);

    const canSend = useMemo(() => !!conversation && !!token, [conversation, token]);

    const stopStream = () => {
        if (streamRef.current) {
            streamRef.current.close();
            streamRef.current = null;
        }
    };

    const loadMessages = async () => {
        if (!conversationId || !token) return;
        try {
            const response = await api.get(`/chat/conversations/${conversationId}/messages?token=${encodeURIComponent(token)}`);
            setConversation(response?.data?.conversation || null);
            setListing(response?.data?.listing || null);
            setRecipientPhone(String(response?.data?.recipientPhone || '').trim());
            setMessages(Array.isArray(response?.data?.messages) ? response.data.messages : []);
        } catch (e) {
            setError('Unable to load this guest chat.');
        }
    };

    const startStream = () => {
        stopStream();
        if (!conversationId || !token) return;

        const backendUrl = String(process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
        if (!backendUrl) return;

        const stream = new EventSource(`${backendUrl}/chat/conversations/${conversationId}/stream?token=${encodeURIComponent(token)}`);

        stream.addEventListener('message', (event) => {
            try {
                const payload = JSON.parse(event.data);
                setMessages((prev) => {
                    if (prev.some((item) => Number(item.id) === Number(payload.id))) return prev;
                    return [...prev, payload];
                });
            } catch (_) {
                // Ignore malformed events.
            }
        });

        stream.onerror = () => {
            stream.close();
        };

        streamRef.current = stream;
    };

    useEffect(() => {
        loadMessages();
        startStream();
        return () => stopStream();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId, token]);

    const send = async (event) => {
        event.preventDefault();
        const trimmed = composer.trim();
        if (!trimmed || !canSend) return;

        try {
            const response = await api.post(`/chat/conversations/${conversationId}/messages`, {
                token,
                body: trimmed
            });
            setComposer('');
            setMessages((prev) => {
                if (prev.some((item) => Number(item.id) === Number(response?.data?.id))) return prev;
                return [...prev, response.data];
            });
        } catch (e) {
            setError('Unable to send message.');
        }
    };

    const sendAsText = async (event) => {
        event.preventDefault();
        const trimmed = composer.trim();
        if (!trimmed || !canSend || !recipientPhone) return;

        try {
            const response = await api.post(`/chat/conversations/${conversationId}/messages`, {
                token,
                body: trimmed
            });
            setComposer('');
            setMessages((prev) => {
                if (prev.some((item) => Number(item.id) === Number(response?.data?.id))) return prev;
                return [...prev, response.data];
            });
            window.location.href = `sms:${recipientPhone}?body=${encodeURIComponent(trimmed)}`;
        } catch (e) {
            setError('Unable to send message.');
        }
    };

    return (
        <div className="chat-page guest-chat-page">
            <div className="chat-thread chat-thread-full">
                {listing && (
                    <div className="guest-chat-listing-hero">
                        {listing.heroImageUrl && (
                            <div
                                className="guest-chat-listing-hero-image"
                                style={{ backgroundImage: `url(${listing.heroImageUrl})` }}
                                aria-hidden="true"
                            />
                        )}
                        <div className="guest-chat-listing-hero-copy">
                            <p className="guest-chat-listing-label">Your stay</p>
                            <h2>{listing.title || 'Property stay'}</h2>
                            {listing.shortDescription && <p>{listing.shortDescription}</p>}
                        </div>
                    </div>
                )}

                <div className="chat-thread-header">
                    <div className="chat-thread-header-main">
                        <span className="chat-conversation-avatar chat-conversation-avatar-large">{toInitials(conversation?.guestName || conversation?.guestEmail || 'Guest')}</span>
                        <div>
                            <p className="chat-kicker">Direct booking support</p>
                            <h3>{conversation?.guestName || 'Guest Chat'}</h3>
                            <span>{conversation?.guestEmail || ''}</span>
                        </div>
                    </div>
                </div>

                {error && <p className="chat-error">{error}</p>}

                <div className="chat-messages">
                    {messages.map((entry) => (
                        <article key={entry.id} className={`chat-bubble ${entry.senderRole === 'guest' ? 'chat-bubble-guest' : 'chat-bubble-host'}`}>
                            <header>
                                <strong>{entry.senderDisplay}</strong>
                                <small>{toShortDate(entry.createdAt)}</small>
                            </header>
                            <p>{entry.body}</p>
                        </article>
                    ))}
                    {messages.length === 0 && <p className="chat-empty">No messages yet.</p>}
                </div>

                <form className="chat-composer" onSubmit={send}>
                    <textarea value={composer} onChange={(event) => setComposer(event.target.value)} rows={3} placeholder="Ask your host a question..." />
                    <div className="chat-composer-actions">
                        <button className="btn-primary" type="submit" disabled={!canSend}>Send</button>
                        {isMobile && recipientPhone && (
                            <button className="btn-secondary" type="button" disabled={!canSend} onClick={sendAsText}>Send as Text</button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default GuestChatPage;
